import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, Award, Check, X, Download, CheckCheck, RefreshCw, Plus, Pencil, Trash2,
  Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, AlignJustify,
} from "lucide-react";
import { api, apiErrorMessage } from "../../lib/api";
import ExportButton from "./ExportButton";
import { downloadBlob } from "../../lib/downloadBlob";
import { exportName, exportPath } from "../../lib/exportFile";
import Pagination from "./Pagination";

const STATUS_LABELS = { pending: "În așteptare", issued: "Emisă", rejected: "Respinsă", approved: "Aprobată" };
const STATUS_COLORS = {
  pending: "bg-[#F5C76E]/30 text-[#7A5A1B]",
  issued: "bg-[#519872]/15 text-[#2F6B4E]",
  rejected: "bg-[#E63946]/15 text-[#7A1F26]",
  approved: "bg-[#3F8EBA]/15 text-[#1f5d80]",
};
const PAGE_SIZE = 25;

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleDateString("ro-RO", { day: "2-digit", month: "2-digit", year: "numeric" });
};

const htmlHasText = (htmlStr) => {
  const div = document.createElement("div");
  div.innerHTML = htmlStr || "";
  return (div.textContent || "").replace(/ /g, " ").trim().length > 0;
};

// Lightweight rich-text field (contentEditable + toolbar). Emits HTML; the
// backend sanitizes it to an xhtml2pdf-safe subset. Uncontrolled after mount
// (sets initial value once) so the caret never jumps while typing.
function RichTextField({ value, onChange, testid }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) ref.current.innerHTML = value || "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const exec = (cmd) => {
    document.execCommand(cmd, false, null);
    if (ref.current) { ref.current.focus(); onChange(ref.current.innerHTML); }
  };
  const tb = "px-2 py-1 rounded hover:bg-[#EDEEF0] text-[#373A45]";
  const keep = (e) => e.preventDefault();
  const Sep = () => <span className="mx-1 h-4 w-px bg-[#E5E7EB] inline-block align-middle" />;
  return (
    <div className="border border-[#E5E7EB] rounded-lg overflow-hidden">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-[#E5E7EB] bg-[#F9FAFB] px-1 py-1">
        <button type="button" onMouseDown={keep} onClick={() => exec("bold")} className={tb} title="Bold"><Bold size={15} /></button>
        <button type="button" onMouseDown={keep} onClick={() => exec("italic")} className={tb} title="Italic"><Italic size={15} /></button>
        <button type="button" onMouseDown={keep} onClick={() => exec("underline")} className={tb} title="Subliniat"><Underline size={15} /></button>
        <Sep />
        <button type="button" onMouseDown={keep} onClick={() => exec("justifyLeft")} className={tb} title="Aliniere stânga"><AlignLeft size={15} /></button>
        <button type="button" onMouseDown={keep} onClick={() => exec("justifyCenter")} className={tb} title="Centrat"><AlignCenter size={15} /></button>
        <button type="button" onMouseDown={keep} onClick={() => exec("justifyRight")} className={tb} title="Aliniere dreapta"><AlignRight size={15} /></button>
        <button type="button" onMouseDown={keep} onClick={() => exec("justifyFull")} className={tb} title="Justify"><AlignJustify size={15} /></button>
      </div>
      <div ref={ref} contentEditable suppressContentEditableWarning data-testid={testid}
        onInput={() => onChange(ref.current.innerHTML)}
        className="px-3 py-2 text-sm outline-none text-[#373A45] leading-relaxed"
        style={{ minHeight: 150, textAlign: "justify" }} />
    </div>
  );
}

// `canManage`: owner and admin FPED, as the server allows (19.10.2026: the buttons showed to every role).
export default function CertificatesTab({ isOwner = false, canManage = isOwner }) {
  const [view, setView] = useState("requests"); // "requests" | "registry"
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [webinars, setWebinars] = useState([]);
  const [webinarId, setWebinarId] = useState("");
  const [status, setStatus] = useState("all");
  const [attended, setAttended] = useState("all");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sort, setSort] = useState("created_desc");
  const [busyId, setBusyId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({ full_name: "", school: "", title: "", body: "" });
  const [manualBusy, setManualBusy] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({ full_name: "", school: "", custom_title: "", custom_body: "" });
  const [editBusy, setEditBusy] = useState(false);

  const isRegistry = view === "registry";
  const effStatus = isRegistry ? "issued" : status;
  const effAttended = isRegistry ? "all" : attended;

  useEffect(() => {
    api.get("/admin/webinars", { params: { include_past: true } })
      .then((r) => setWebinars(r.data?.webinars || []))
      .catch(() => {});
  }, []);

  // Each sub-tab has its own sensible default sort.
  useEffect(() => { setSort(view === "registry" ? "number_desc" : "created_desc"); }, [view]);

  // Reset to first page whenever the filters, sort or the active sub-tab change.
  useEffect(() => { setPage(1); }, [webinarId, status, attended, q, dateFrom, dateTo, sort, view]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/certificates", {
        params: {
          webinar_id: webinarId || undefined, status: effStatus, attended: effAttended,
          q: q || undefined, date_from: dateFrom || undefined, date_to: dateTo || undefined,
          sort, page, page_size: PAGE_SIZE,
        },
      });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch {
      toast.error("Nu am putut încărca adeverințele.");
    } finally {
      setLoading(false);
    }
  }, [webinarId, effStatus, effAttended, q, dateFrom, dateTo, sort, page]);
  useEffect(() => { load(); }, [load]);

  const approve = async (row) => {
    setBusyId(row.id);
    try {
      const r = await api.post(`/admin/certificates/${row.id}/approve`);
      toast.success(`Adeverință emisă: ${r.data?.number}${r.data?.emailed === false ? " (email nesimulat)" : ""}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la emitere.");
    } finally { setBusyId(null); }
  };

  const approveAnyway = async (row) => {
    if (!window.confirm(`Cererea lui ${row.full_name} a fost respinsă. Emiți totuși adeverința și trimiți PDF-ul pe email?`)) return;
    await approve(row);
  };

  const reject = async (row) => {
    const reason = window.prompt(`Respingi cererea lui ${row.full_name}? Motiv (opțional):`, row.attended ? "" : "Nu figurează ca prezent");
    if (reason === null) return;
    setBusyId(row.id);
    try {
      await api.post(`/admin/certificates/${row.id}/reject`, { reason });
      toast.success("Cerere respinsă.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare.");
    } finally { setBusyId(null); }
  };

  const downloadPdf = async (row) => {
    try {
      const r = await api.get(`/admin/certificates/${row.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Adeverinta-${row.certificate_number}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error("Nu am putut descărca PDF-ul.");
    }
  };

  const exportCsv = (format) => {
    const params = new URLSearchParams();
    const all = { webinar_id: webinarId, status: effStatus, attended: effAttended, q, date_from: dateFrom,
      date_to: dateTo, sort };
    Object.entries(all).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") params.set(k, v); });
    return downloadBlob(exportPath("/admin/certificates/export", params, format), exportName("adeverinte", format),
      setExporting);
  };

  const submitManual = async (e) => {
    e.preventDefault();
    if (!manualForm.full_name.trim() || !htmlHasText(manualForm.body)) {
      toast.error("Numele și textul adeverinței sunt obligatorii.");
      return;
    }
    setManualBusy(true);
    try {
      const r = await api.post("/admin/certificates/manual", {
        full_name: manualForm.full_name.trim(),
        school: manualForm.school.trim(),
        title: manualForm.title.trim(),
        body: manualForm.body,
      });
      toast.success(`Adeverință manuală emisă: ${r.data?.number}. O găsești în Registru.`);
      setManualOpen(false);
      setManualForm({ full_name: "", school: "", title: "", body: "" });
      setView("registry");
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Eroare la emiterea adeverinței manuale."));
    } finally { setManualBusy(false); }
  };

  const openEdit = (row) => {
    setEditForm({
      full_name: row.full_name || "", school: row.school || "",
      custom_title: row.custom_title || "", custom_body: row.custom_body || "",
    });
    setEditRow(row);
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editRow) return;
    setEditBusy(true);
    try {
      const body = { full_name: editForm.full_name, school: editForm.school };
      if (editRow.manual) { body.custom_title = editForm.custom_title; body.custom_body = editForm.custom_body; }
      await api.patch(`/admin/certificates/${editRow.id}`, body);
      toast.success("Adeverință actualizată.");
      setEditRow(null);
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Eroare la editare."));
    } finally { setEditBusy(false); }
  };

  const remove = async (row) => {
    if (!window.confirm(`Ștergi adeverința${row.certificate_number ? " " + row.certificate_number : ""} a lui ${row.full_name}?\nDispare din listă, registru și din verificarea online. (recuperabilă)`)) return;
    setBusyId(row.id);
    try {
      await api.delete(`/admin/certificates/${row.id}`);
      toast.success("Adeverință ștearsă.");
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Eroare la ștergere."));
    } finally { setBusyId(null); }
  };

  const bulkReject = async () => {
    if (!webinarId) { toast.error("Alege întâi un webinar pentru respingerea în masă."); return; }
    const pendingAbsent = items.filter((i) => i.status === "pending" && !i.attended).length;
    if (!window.confirm(`Respingi în masă cererile ABSENȚILOR în așteptare pentru acest webinar?\nFiecare persoană primește un email de respingere.\n(${pendingAbsent} pe pagina curentă; se procesează toate din DB)`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/certificates/bulk-reject", { webinar_id: webinarId, only_absent: true, reason: "Nu figurează ca prezent" });
      toast.success(`Respingere pornită pentru ${r.data?.candidates} cereri. Emailurile pleacă în fundal.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la respingerea în masă.");
    } finally { setBulkBusy(false); }
  };

  const bulkApprove = async () => {
    if (!webinarId) { toast.error("Alege întâi un webinar pentru aprobarea în masă."); return; }
    const pendingAttended = items.filter((i) => i.status === "pending" && i.attended).length;
    if (!window.confirm(`Aprobi în masă toate cererile PREZENȚILOR în așteptare pentru acest webinar?\n(${pendingAttended} pe pagina curentă; se procesează toate din DB)`)) return;
    setBulkBusy(true);
    try {
      const r = await api.post("/admin/certificates/bulk-approve", { webinar_id: webinarId, only_attended: true });
      // Issued in the background (19.10.2026): the list fills in as they go out.
      const n = r.data?.candidates || 0;
      toast.success(n === 0 ? "Nu e nicio cerere de aprobat."
        : `Se emit acum ${n === 1 ? "o adeverință" : `${n} adeverințe`}. Lista se actualizează pe măsură ce pleacă.`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la aprobarea în masă.");
    } finally { setBulkBusy(false); }
  };

  const rowActions = (row) => (
    <>
      {canManage && (
        <button onClick={() => openEdit(row)} title="Editează"
          className="inline-flex items-center gap-1 text-[#6B7280] hover:text-[#373A45] text-xs font-semibold ml-3"
          data-testid={`certificate-edit-${row.id}`}>
          <Pencil size={14} /> Editează
        </button>
      )}
      {isOwner && (
        <button onClick={() => remove(row)} disabled={busyId === row.id} title="Șterge"
          className="inline-flex items-center gap-1 text-[#E63946] hover:text-[#a52833] text-xs font-semibold ml-3 disabled:opacity-50"
          data-testid={`certificate-delete-${row.id}`}>
          <Trash2 size={14} /> Șterge
        </button>
      )}
    </>
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const subTab = (key, label) => (
    <button
      onClick={() => setView(key)}
      data-testid={`certificates-subtab-${key}`}
      className={`px-4 py-2 text-sm font-semibold rounded-full transition-colors ${
        view === key ? "bg-[#965EA7] text-white" : "text-[#6B7280] hover:text-[#373A45] hover:bg-[#F1F1F1]"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div data-testid="certificates-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#373A45] inline-flex items-center gap-2">
            <Award size={20} className="text-[#965EA7]" /> Adeverințe
          </h2>
          <p className="text-sm text-[#6B7280] mt-0.5">
            {isRegistry ? "Registrul adeverințelor emise" : "Cereri de adeverință de participare"} · {total} total
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={load} disabled={loading}
            className="inline-flex items-center gap-2 rounded-full border border-[#E5E7EB] hover:bg-[#F9FAFB] text-[#373A45] font-semibold px-4 py-2 text-sm disabled:opacity-50"
            data-testid="certificates-refresh" title="Reîncarcă lista">
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} /> Reîmprospătează
          </button>
          <ExportButton onExport={exportCsv} busy={exporting} label="Descarcă"
            className="inline-flex items-center gap-2 rounded-full border border-[#E5E7EB] hover:bg-[#F9FAFB] text-[#373A45] font-semibold px-4 py-2 text-sm disabled:opacity-50"
            testid="certificates-export-csv" />
          {canManage && (
            <button onClick={() => setManualOpen(true)}
              className="inline-flex items-center gap-2 rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white font-semibold px-4 py-2 text-sm"
              data-testid="certificates-manual-open">
              <Plus size={16} /> Adeverință manuală
            </button>
          )}
          {canManage && !isRegistry && (
            <>
              <button onClick={bulkReject} disabled={bulkBusy || !webinarId}
                className="inline-flex items-center gap-2 rounded-full bg-[#E63946] hover:bg-[#c22f3b] text-white font-semibold px-4 py-2 text-sm disabled:opacity-50"
                data-testid="certificates-bulk-reject">
                {bulkBusy ? <Loader2 className="animate-spin" size={16} /> : <X size={16} />} Respinge în masă (absenți)
              </button>
              <button onClick={bulkApprove} disabled={bulkBusy || !webinarId}
                className="inline-flex items-center gap-2 rounded-full bg-[#519872] hover:bg-[#3f7a5b] text-white font-semibold px-4 py-2 text-sm disabled:opacity-50"
                data-testid="certificates-bulk-approve">
                {bulkBusy ? <Loader2 className="animate-spin" size={16} /> : <CheckCheck size={16} />} Aprobă în masă (prezenți)
              </button>
            </>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4 bg-[#F9FAFB] border border-[#E5E7EB] rounded-full p-1 w-fit">
        {subTab("requests", "Cereri")}
        {subTab("registry", "Registru")}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <select value={webinarId} onChange={(e) => setWebinarId(e.target.value)} className="rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm">
          <option value="">Toate webinariile</option>
          {webinars.map((w) => <option key={w.id} value={w.id}>{w.title} ({w.date})</option>)}
        </select>
        {!isRegistry && (
          <>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm">
              <option value="all">Toate statusurile</option>
              <option value="pending">În așteptare</option>
              <option value="issued">Emise</option>
              <option value="rejected">Respinse</option>
            </select>
            <select value={attended} onChange={(e) => setAttended(e.target.value)} className="rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm">
              <option value="all">Prezent: toți</option>
              <option value="yes">Doar prezenți</option>
              <option value="no">Doar absenți</option>
            </select>
          </>
        )}
        <select value={sort} onChange={(e) => setSort(e.target.value)}
          className="rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="certificates-sort">
          {isRegistry ? (
            <>
              <option value="number_desc">Nr. adeverință (desc.)</option>
              <option value="number_asc">Nr. adeverință (asc.)</option>
              <option value="issued_desc">Data emiterii (noi)</option>
              <option value="issued_asc">Data emiterii (vechi)</option>
            </>
          ) : (
            <>
              <option value="created_desc">Data cererii (noi)</option>
              <option value="created_asc">Data cererii (vechi)</option>
            </>
          )}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Caută nume / email / școală"
          className="flex-1 min-w-[200px] rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm" />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4 text-sm text-[#6B7280]">
        <span>Data cererii:</span>
        <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
          className="rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="certificates-date-from" />
        <span>–</span>
        <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
          className="rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="certificates-date-to" />
        {(dateFrom || dateTo) && (
          <button onClick={() => { setDateFrom(""); setDateTo(""); }}
            className="inline-flex items-center gap-1 text-[#965EA7] hover:text-[#7A4986] font-semibold"
            data-testid="certificates-date-clear">
            <X size={14} /> Șterge intervalul
          </button>
        )}
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          {isRegistry ? (
            <table className="w-full text-sm">
              <thead className="bg-[#F9FAFB] text-[#6B7280] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Nr. adeverință</th>
                  <th className="text-left px-4 py-3">Nume / Email</th>
                  <th className="text-left px-4 py-3">Webinar</th>
                  <th className="text-left px-4 py-3">Data emiterii</th>
                  <th className="text-left px-4 py-3">Emis de</th>
                  <th className="text-left px-4 py-3">Cod verificare</th>
                  <th className="text-right px-4 py-3">PDF</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F1F1]">
                {loading ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-[#6B7280]"><Loader2 className="inline animate-spin" size={16} /> Se încarcă...</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-10 text-center text-[#6B7280]">Nicio adeverință emisă.</td></tr>
                ) : items.map((row) => (
                  <tr key={row.id} className="hover:bg-[#FAFAFB]" data-testid={`certificate-registry-row-${row.id}`}>
                    <td className="px-4 py-3 font-mono text-[#373A45]">{row.certificate_number || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#373A45]">{row.full_name}</div>
                      <div className="text-xs text-[#9CA3AF]">{row.email}</div>
                      <div className="text-xs text-[#9CA3AF]">{row.school}</div>
                    </td>
                    <td className="px-4 py-3 text-[#373A45]">{row.webinar_title}</td>
                    <td className="px-4 py-3 text-[#373A45]">{fmtDate(row.issued_at)}</td>
                    <td className="px-4 py-3 text-xs text-[#6B7280]">{row.issued_by || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs text-[#6B7280]">{row.verification_code || "—"}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button onClick={() => downloadPdf(row)}
                        className="inline-flex items-center gap-1 text-[#965EA7] hover:text-[#7A4986] text-xs font-semibold"
                        data-testid={`certificate-pdf-${row.id}`}>
                        <Download size={14} /> PDF
                      </button>
                      {rowActions(row)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#F9FAFB] text-[#6B7280] text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-3">Nume / Email</th>
                  <th className="text-left px-4 py-3">Webinar</th>
                  <th className="text-left px-4 py-3">Prezent</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Acțiuni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F1F1]">
                {loading ? (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-[#6B7280]"><Loader2 className="inline animate-spin" size={16} /> Se încarcă...</td></tr>
                ) : items.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-[#6B7280]">Nicio cerere.</td></tr>
                ) : items.map((row) => (
                  <tr key={row.id} className="hover:bg-[#FAFAFB]" data-testid={`certificate-row-${row.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-[#373A45]">{row.full_name}</div>
                      <div className="text-xs text-[#9CA3AF]">{row.email}</div>
                      <div className="text-xs text-[#9CA3AF]">{row.school}</div>
                    </td>
                    <td className="px-4 py-3 text-[#373A45]">{row.webinar_title}</td>
                    <td className="px-4 py-3">
                      {row.attended
                        ? <span className="inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold bg-[#519872]/15 text-[#2F6B4E]">Da</span>
                        : <span className="inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold bg-[#E63946]/15 text-[#7A1F26]">Absent</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_COLORS[row.status] || ""}`}>
                        {STATUS_LABELS[row.status] || row.status}
                      </span>
                      {row.certificate_number && <div className="text-[11px] text-[#9CA3AF] mt-1 font-mono">{row.certificate_number}</div>}
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {canManage && row.status === "pending" && (
                        <>
                          <button onClick={() => approve(row)} disabled={busyId === row.id}
                            className="inline-flex items-center gap-1 text-[#519872] hover:text-[#3f7a5b] text-xs font-semibold mr-3 disabled:opacity-50"
                            data-testid={`certificate-approve-${row.id}`}>
                            {busyId === row.id ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} Aprobă
                          </button>
                          <button onClick={() => reject(row)} disabled={busyId === row.id}
                            className="inline-flex items-center gap-1 text-[#E63946] hover:text-[#a52833] text-xs font-semibold">
                            <X size={14} /> Respinge
                          </button>
                        </>
                      )}
                      {canManage && row.status === "rejected" && (
                        <button onClick={() => approveAnyway(row)} disabled={busyId === row.id}
                          className="inline-flex items-center gap-1 text-[#519872] hover:text-[#3f7a5b] text-xs font-semibold disabled:opacity-50"
                          data-testid={`certificate-approve-anyway-${row.id}`}>
                          {busyId === row.id ? <Loader2 className="animate-spin" size={14} /> : <Check size={14} />} Aprobă totuși
                        </button>
                      )}
                      {row.status === "issued" && (
                        <button onClick={() => downloadPdf(row)}
                          className="inline-flex items-center gap-1 text-[#965EA7] hover:text-[#7A4986] text-xs font-semibold"
                          data-testid={`certificate-pdf-${row.id}`}>
                          <Download size={14} /> PDF
                        </button>
                      )}
                      {rowActions(row)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <Pagination
          page={page} pageSize={PAGE_SIZE} total={total} totalPages={totalPages}
          hasNext={page < totalPages} hasPrev={page > 1}
          onPageChange={setPage} testid="certificates-pagination"
        />
      </div>

      {manualOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="certificates-manual-modal">
          <form onSubmit={submitManual} className="bg-white rounded-2xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-bold text-[#373A45] inline-flex items-center gap-2">
                <Award size={18} className="text-[#965EA7]" /> Adeverință manuală
              </h3>
              <button type="button" onClick={() => setManualOpen(false)} className="text-[#6B7280] hover:text-[#373A45]"><X size={20} /></button>
            </div>
            <p className="text-sm text-[#6B7280]">
              Emite o adeverință specială, cu număr în registru și verificare QR. Nu se trimite email — o descarci din Registru.
            </p>
            <div>
              <label className="block text-sm font-semibold text-[#373A45] mb-1">Nume complet *</label>
              <input value={manualForm.full_name} onChange={(e) => setManualForm((f) => ({ ...f, full_name: e.target.value }))}
                className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="manual-full-name" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#373A45] mb-1">Școală / Instituție</label>
              <input value={manualForm.school} onChange={(e) => setManualForm((f) => ({ ...f, school: e.target.value }))}
                className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="manual-school" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#373A45] mb-1">Titlu document (gol → „ADEVERINȚĂ")</label>
              <input value={manualForm.title} onChange={(e) => setManualForm((f) => ({ ...f, title: e.target.value }))}
                placeholder="ADEVERINȚĂ" className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="manual-title" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#373A45] mb-1">Text adeverință *</label>
              <RichTextField value={manualForm.body} onChange={(v) => setManualForm((f) => ({ ...f, body: v }))} testid="manual-body" />
              <p className="text-xs text-[#9CA3AF] mt-1">Poți îngroșa/înclina textul și alinia paragrafele. Implicit textul e justify.</p>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setManualOpen(false)} className="px-4 py-2 rounded-full text-[#6B7280] hover:text-[#373A45] text-sm">Anulează</button>
              <button type="submit" disabled={manualBusy}
                className="inline-flex items-center gap-2 rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white font-semibold px-4 py-2 text-sm disabled:opacity-50"
                data-testid="manual-submit">
                {manualBusy ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />} Emite adeverința
              </button>
            </div>
          </form>
        </div>
      )}

      {editRow && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="certificates-edit-modal">
          <form onSubmit={submitEdit} className="bg-white rounded-2xl w-full max-w-lg p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="font-display text-xl font-bold text-[#373A45] inline-flex items-center gap-2">
                <Pencil size={18} className="text-[#965EA7]" /> Editează adeverința
              </h3>
              <button type="button" onClick={() => setEditRow(null)} className="text-[#6B7280] hover:text-[#373A45]"><X size={20} /></button>
            </div>
            {editRow.certificate_number && (
              <p className="text-sm text-[#6B7280]">
                <span className="font-mono">{editRow.certificate_number}</span> — modificările se văd la următoarea descărcare a PDF-ului și în verificarea online.
              </p>
            )}
            <div>
              <label className="block text-sm font-semibold text-[#373A45] mb-1">Nume complet</label>
              <input value={editForm.full_name} onChange={(e) => setEditForm((f) => ({ ...f, full_name: e.target.value }))}
                className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="edit-full-name" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-[#373A45] mb-1">Școală / Instituție</label>
              <input value={editForm.school} onChange={(e) => setEditForm((f) => ({ ...f, school: e.target.value }))}
                className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="edit-school" />
            </div>
            {editRow.manual && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-[#373A45] mb-1">Titlu document</label>
                  <input value={editForm.custom_title} onChange={(e) => setEditForm((f) => ({ ...f, custom_title: e.target.value }))}
                    placeholder="ADEVERINȚĂ" className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm" data-testid="edit-title" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#373A45] mb-1">Text adeverință</label>
                  <RichTextField value={editForm.custom_body} onChange={(v) => setEditForm((f) => ({ ...f, custom_body: v }))} testid="edit-body" />
                </div>
              </>
            )}
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={() => setEditRow(null)} className="px-4 py-2 rounded-full text-[#6B7280] hover:text-[#373A45] text-sm">Anulează</button>
              <button type="submit" disabled={editBusy}
                className="inline-flex items-center gap-2 rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white font-semibold px-4 py-2 text-sm disabled:opacity-50"
                data-testid="edit-submit">
                {editBusy ? <Loader2 className="animate-spin" size={16} /> : <Check size={16} />} Salvează
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
